import { useCallback, useState } from "react";
import { PHASES, phaseForHour } from "@/data/phases";
import type { Phase } from "@/data/phases";

export interface PhaseControl {
  phase: Phase;
  /** True when following the real clock; false when manually overridden. */
  isAuto: boolean;
  /** Advance to the next phase (enters manual/preview mode). */
  cycle: () => void;
  /** Drop back to following the real time of day. */
  resetAuto: () => void;
}

/**
 * Picks the active phase from the live `hour`, but lets the user override it
 * to preview other times of day (the "always changing" toy). Overriding pauses
 * auto-follow until reset.
 */
export function usePhase(hour: number): PhaseControl {
  const [override, setOverride] = useState<string | null>(null);

  const autoPhase = phaseForHour(hour);
  const phase = (override && PHASES.find((p) => p.id === override)) || autoPhase;

  const cycle = useCallback(() => {
    setOverride((prev) => {
      const current = prev ?? phaseForHour(hour).id;
      const i = PHASES.findIndex((p) => p.id === current);
      return PHASES[(i + 1) % PHASES.length]!.id;
    });
  }, [hour]);

  const resetAuto = useCallback(() => setOverride(null), []);

  return { phase, isAuto: override === null, cycle, resetAuto };
}
